import React, { useEffect, useMemo, useState } from 'react';
import { PageLayout } from '@Local/Components/Layout';
import { Grid } from '@mui/material';
import { Box, Card, Stack, Typography } from '@curves/ui-base';
import { Overview } from '@curves/ui-shared';

import { parseDate } from '@Util/moment.util';
import { formatCurrencyFn } from '@Util/utils';
import { getDeviceRentalBillingForFailureRowLabel } from '@Local/DeviceRental/Components/DeviceRentalBillingTemplate';
import { Link, useParams } from 'react-router-dom';
import { DEVICE_RENTAL_BATCH_PAYMENT_OVERVIEW_ROUTER } from '@Router/routerPath';
import useDeviceOrderApi from '@Apis/useDeviceOrderApi';
import {DATE_TIME_STANDARD} from '@Constants/appConfig';
/**
 * 帳單資訊欄位配置
 * @type {Array<Object>}
 */
const billingInfo = [
    {
        key: 'taskStatusText',
        text: '狀態',
    },
    {
        key: 'settleDate',
        text: '送件扣款日期',
        formatter: (value) => parseDate(value, DATE_TIME_STANDARD.DISPLAYDATEBYSLASH),
    },
    {
        key: 'totalAmount',
        text: '總筆數',
    },
    {
        key: 'totalPrice',
        text: '扣款金額',
        formatter: (value) => formatCurrencyFn(value, '$'),
    },
    {
        key: 'settleUserDisplayName',
        text: '扣款人員',
    },
];
/**
 * 處理欄位值顯示
 * @param {*} value - 原始值
 * @param {Function} formatter - 格式化函數
 * @returns {*} 格式化後的值或預設值
 */
const handleValue = (value, formatter) => {
    if (value === undefined || value === null) return '-';
    if (!formatter) return value;
    if (typeof formatter === 'function') return formatter(value);
};
/**
 * 設備租賃帳單明細頁面
 *
 * 功能：
 * - 顯示特定任務的扣款明細資訊
 * - 展示扣款成功/失敗統計
 * - 列出扣款失敗的詳細清單
 *
 * @component
 * @returns {JSX.Element} 設備租賃帳單明細頁面
 */
const DeviceRentalBillingPage = () => {
    const { maskID } = useParams();
    const { getDeviceOrderTasksApi } = useDeviceOrderApi();
    const [sourceData, setSourceData] = useState({});
    /**
     * 取得設備訂單任務資料
     * @param {string} maskID - 任務遮罩 ID
     */
    const getDeviceOrderTasks = async (maskID) => {
        const resp = await getDeviceOrderTasksApi(maskID);
        if (resp) {
            setSourceData(resp);
        }
    };
    useEffect(
        () => {
            if (!maskID) return;
            getDeviceOrderTasks(maskID);
        },
        // eslint-disable-next-line
        [maskID],
    );
    const headerRow = useMemo(
        () => {
            return getDeviceRentalBillingForFailureRowLabel();
        },
        // eslint-disable-next-line
        [],
    );
    const pageActions = useMemo(
        () => [
            {
                label: '返回總覽',
                variant: 'contained',
                as: Link,
                to: DEVICE_RENTAL_BATCH_PAYMENT_OVERVIEW_ROUTER,
            },
        ],
        [],
    );
    return (
        <PageLayout>
            <PageLayout.Header title="銀行扣款明細" actions={pageActions} />
            <Card className={'mb-1 p-3'}>
                <Card.CardContent>
                    <Grid spacing={2} container>
                        {billingInfo.map((item, i) => {
                            const { key, text, formatter } = item;
                            return (
                                <Grid key={`${key}-${i}`} xs={4} item>
                                    <Grid container>
                                        <Grid xs={5} item>
                                            <Box className={'font-color-3'}>{text}</Box>
                                        </Grid>
                                        <Grid xs={7} item>
                                            <Box>{handleValue(sourceData[key], formatter)}</Box>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Card.CardContent>
            </Card>
            <Card className={'mb-1 px-4 py-3'}>
                <Card.CardContent>
                    <Grid spacing={2} container>
                        <Grid xs={3} item>
                            <Stack className={'h-100'} alignItems={'center'}>
                                <Grid container>
                                    <Grid xs={5} item>
                                        <Box className={'text-error'}>失敗筆數</Box>
                                    </Grid>
                                    <Grid xs={7} item>
                                        <Box className={'text-error'}>{sourceData?.settleFailureAmount || '-'}</Box>
                                    </Grid>
                                </Grid>
                            </Stack>
                        </Grid>
                        <Grid xs={3} item>
                            <Stack className={'h-100'} alignItems={'center'}>
                                <Grid container>
                                    <Grid xs={5} item>
                                        <Box className={'text-success'}>成功筆數</Box>
                                    </Grid>
                                    <Grid xs={7} item>
                                        <Box className={'text-success'}>{sourceData?.settleSuccessAmount || '-'}</Box>
                                    </Grid>
                                </Grid>
                            </Stack>
                        </Grid>
                        <Grid xs={6} item>
                            <Stack alignItems={'center'} justifyContent={'end'}>
                                <Box className={'mr-4 font-color-3'}>
                                    實際扣款金額
                                    <Box component={'span'} className={'h5 ml-2 font-weight-bold font-color-1'}>
                                        {sourceData?.settleSuccessPrice ? formatCurrencyFn(sourceData.settleSuccessPrice, '$') : '-'}
                                    </Box>
                                </Box>
                            </Stack>
                        </Grid>
                    </Grid>
                </Card.CardContent>
            </Card>
            <Card className={'px-3'}>
                <Card.CardContent>
                    <Overview contextValue={{ overviewData: { list: sourceData?.failureRecords || [] } }}>
                        <Stack className={'overview__toolbar'} alignItems={'center'}>
                            <Typography variant={'h5'} className={'font-weight-bold'}>
                                扣款失敗清單
                            </Typography>
                        </Stack>
                        <Overview.Table headerRow={headerRow} />
                    </Overview>
                </Card.CardContent>
            </Card>
        </PageLayout>
    );
};
export default DeviceRentalBillingPage;
